import React, { Component } from 'react';
import { string, func, object, bool, array, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import hostedFields from 'braintree-web/hosted-fields';
import client from 'braintree-web/client';

import {
  Button,
  SimpleDialog,
  Modal,
  CircleInfoIcon,
} from '@snapask/common-components';

import whiteDialogTheme from '@snapask/common-components/styles/simpleDialogTheme/whiteNoPadding.scss';
import Gray from '@snapask/common-components/styles/buttonTheme/gray.scss';

import { HKD, SGD } from '@dragonfruit/constants/currency';
import { actions as reportActions } from '@dragonfruit/redux/report';
import * as loadingState from '@dragonfruit/constants/loadingState';

import {
  fetchBraintreeClientToken,
  fetchPaymentMethodToken,
  getBraintreeClientToken,
  getPaymentMethodTokens,
} from '@dragonfruit/redux/purchase/braintreeSetup';

import BraintreeLoader from './components/BraintreeLoader';

import { RENEWABLE } from '@dragonfruit/constants/planTypes';
import styles from './BraintreeCheckout.scss';

const cx = require('classnames/bind').bind(styles);

const mapStateToProps = state => ({
  braintreeClientToken: getBraintreeClientToken(state),
  braintreePaymentMethods: getPaymentMethodTokens(state),
});

// card types
const VISA = 'visa';
const AMEX = 'american-express';
const MASTER_CARD = 'master-card';

class BraintreeCheckout extends Component {
  static propTypes = {
    braintreeClientToken: string.isRequired,
    fetchBraintreeClientToken: func.isRequired,
    fetchPaymentMethodToken: func.isRequired,
    loadingStatus: string.isRequired,
    selectedPlan: object.isRequired,
    setReadyToSubmit: func.isRequired,
    t: func.isRequired,
    toggleReportForm: func.isRequired,
    triggerSubmit: bool.isRequired,
    onPaymentSubmit: func.isRequired,
    braintreePaymentMethods: array,
    location: shape({
      search: string,
    }),
  };

  static contextTypes = {
    region: string,
  };

  static defaultProps = {
    braintreePaymentMethods: [],
    location: {},
  };

  state = {
    selectedCard: '',
    invalidFields: [],
    showLoader: false,
    showErrorModal: false,
  };

  componentDidMount() {
    const {
      fetchBraintreeClientToken,
      fetchPaymentMethodToken,
      setReadyToSubmit,
    } = this.props;

    setReadyToSubmit(false);
    fetchBraintreeClientToken();
    fetchPaymentMethodToken();
  }

  componentWillReceiveProps(nextProps) {
    const { setReadyToSubmit } = this.props;
    const { triggerSubmit, braintreeClientToken, loadingStatus } = nextProps;

    if (!!braintreeClientToken) {
      this.generateBraintree(braintreeClientToken);
    }

    if (triggerSubmit !== this.props.triggerSubmit && triggerSubmit) {
      this.onSubmitCheckout();
      setReadyToSubmit(false);
    }

    if (loadingStatus === this.props.loadingStatus) {
      return;
    }

    if (loadingStatus === loadingState.LOADING) {
      this.setState({
        showLoader: true,
        showErrorModal: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.hostedFieldsInstance) {
      this.hostedFieldsInstance.teardown();
    }
  }

  onSubmitCheckout = () => {
    const { onPaymentSubmit } = this.props;

    this.hostedFieldsInstance.tokenize((err, payload) => {
      if (err) {
        return;
      }

      onPaymentSubmit(payload.nonce);
    });
  };

  onCloseErrorModal = () => {
    this.setState({ showErrorModal: false });
  };

  onPaymentSuccess = () => {
    this.setState({
      showLoader: false,
    });
  };

  onPaymentError = () => {
    this.setState({
      showErrorModal: true,
      showLoader: false,
    });
  };

  onClickRetryPayment = () => {
    this.setState({
      showErrorModal: false,
    });
    this.onSubmitCheckout();
  };

  generateBraintree = braintreeClientToken => {
    client.create(
      {
        authorization: braintreeClientToken,
      },
      (clientErr, clientInstance) => {
        if (clientErr) {
          return;
        }

        hostedFields.create(
          {
            client: clientInstance,
            styles: {
              input: {
                'font-size': '16px',
                'font-family': 'inherit',
                color: 'black',
              },
              ':focus': {
                color: 'black',
              },
              '.valid': {
                color: 'black',
              },
              '.invalid': {
                color: '#d9534f',
              },
            },
            fields: {
              number: {
                selector: '#cardNumber',
                placeholder: 'Card Number',
              },
              cvv: {
                selector: '#cvv',
                placeholder: 'CVV',
              },
              expirationYear: {
                selector: '#expirationYear',
                placeholder: 'YY',
              },
              expirationMonth: {
                selector: '#expirationMonth',
                placeholder: 'MM',
              },
            },
          },
          (hostedFieldErr, hostedFieldsInstance) => {
            if (hostedFieldErr) {
              return;
            }

            // Setting hostedFieldInstance as global variable
            this.hostedFieldsInstance = hostedFieldsInstance;
            const hostedFieldFocusClass = 'hosted-field--focus';
            const hostedFieldLabelFocusClass = 'focus';
            const relatedLabelId = id => `${id}-label`;

            hostedFieldsInstance.on('focus', event => {
              const field = event.fields[event.emittedBy];
              const elementId = field.container.id;
              const labelId = relatedLabelId(elementId);

              document
                .getElementById(elementId)
                .classList.add(hostedFieldFocusClass);
              document
                .getElementById(labelId)
                .classList.add(hostedFieldLabelFocusClass);
            });

            hostedFieldsInstance.on('blur', event => {
              const field = event.fields[event.emittedBy];
              const elementId = field.container.id;
              const labelId = relatedLabelId(elementId);

              document
                .getElementById(elementId)
                .classList.remove(hostedFieldFocusClass);
              document
                .getElementById(labelId)
                .classList.remove(hostedFieldLabelFocusClass);
            });

            hostedFieldsInstance.on('cardTypeChange', event => {
              // Change card bg depending on card type
              if (event.cards.length === 1) {
                this.setState({
                  selectedCard: event.cards[0].type,
                });
              }
            });

            hostedFieldsInstance.on('validityChange', event => {
              // Check if all fields are valid, then show submit button
              const formValid = Object.keys(event.fields).every(
                key => event.fields[key].isValid
              );

              const invalidFields = [];

              // Setting valid and invalid fields
              Object.keys(event.fields).forEach(key => {
                if (!event.fields[key].isValid && !event.fields[key].isEmpty) {
                  invalidFields.push(key);
                }
              });

              this.props.setReadyToSubmit(!!formValid);
              this.setState({
                invalidFields: formValid ? [] : invalidFields,
              });
            });

            hostedFieldsInstance.on('empty', () => {
              this.setState({ selectedCard: '' });
            });
          }
        );
      }
    );
  };

  render() {
    const {
      t,
      toggleReportForm,
      loadingStatus,
      selectedPlan: { planType, currency, isFreeTrial },
    } = this.props;

    const {
      selectedCard,
      invalidFields,
      showLoader,
      showErrorModal,
    } = this.state;

    const errorExamples = t('purchase.braintree.errorDialog.examples', {
      returnObjects: true,
      defaultValue: [],
    });

    const allowAmexCard = currency === HKD || currency === SGD;

    const errorString = (
      <div className={styles.errorDialogList}>
        <p>{t('purchase.braintree.errorDialog.subTitle')}</p>
        <ul>
          {errorExamples.map((example, index) => (
            <li key={index}>{example}</li>
          ))}
        </ul>
      </div>
    );

    return (
      <div className={styles.root}>
        <div className={styles.braintreeSecure}>
          <a
            href="https://www.braintreegateway.com/merchants/r78k94zbdsrr68wx/verified"
            target="_blank"
            rel="noreferrer"
          >
            <img src="https://s3.amazonaws.com/braintree-badges/braintree-badge-wide-light.png" />
          </a>
        </div>
        <p className={styles.securityInformation}>
          {t('purchase.securityStatement')}
        </p>
        <div className={styles.containers}>
          {showLoader && (
            <div className={styles.loaderContainer}>
              <BraintreeLoader
                loadingStatus={loadingStatus}
                onSuccess={this.onPaymentSuccess}
                onError={this.onPaymentError}
              />
            </div>
          )}

          {
            <div className={styles.newMethodCard}>
              <form id="cardForm">
                <div className={styles.fieldContainer}>
                  <div className={styles.textfieldFloatLabel}>
                    <label
                      id="cardNumber-label"
                      className={styles.hostedFieldLabel}
                      htmlFor="cardNumber"
                    >
                      {t('purchase.cardNumber')}
                    </label>
                    <div
                      id="cardNumber"
                      className={cx(
                        'hostedField',
                        invalidFields.includes('number') && 'error'
                      )}
                    >
                      <div className={styles.cardImages}>
                        {allowAmexCard && (
                          <div
                            className={cx(
                              'cardImage',
                              'amex',
                              selectedCard && selectedCard !== AMEX && 'hide'
                            )}
                          />
                        )}
                        <div
                          className={cx(
                            'cardImage',
                            'visa',
                            selectedCard && selectedCard !== VISA && 'hide'
                          )}
                        />
                        <div
                          className={cx(
                            'cardImage',
                            'master-card',
                            selectedCard &&
                              selectedCard !== MASTER_CARD &&
                              'hide'
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={cx(
                      'textfieldFloatLabel',
                      'alt',
                      'expirationMonth'
                    )}
                  >
                    <label
                      className={styles.hostedFieldLabel}
                      id="expirationMonth-label"
                    >
                      {t('purchase.expiryMonth')}
                    </label>
                    <div
                      id="expirationMonth"
                      className={cx(
                        'hostedField',
                        invalidFields.includes('expirationMonth') && 'error'
                      )}
                    />
                  </div>

                  <div
                    className={cx(
                      'textfieldFloatLabel',
                      'alt',
                      'expirationYear'
                    )}
                  >
                    <div
                      id="expirationYear"
                      className={cx(
                        'hostedField',
                        invalidFields.includes('expirationYear') && 'error'
                      )}
                    />
                  </div>

                  <div className={cx('textfieldFloatLabel', 'alt', 'cvv')}>
                    <label className={styles.hostedFieldLabel} id="cvv-label">
                      {t('purchase.cvv')}
                    </label>
                    <div
                      id="cvv"
                      className={cx(
                        'hostedField',
                        invalidFields.includes('cvv') && 'error'
                      )}
                    />
                  </div>
                </div>
              </form>
            </div>
          }
          {!isFreeTrial && (
            <div className={styles.subscriptionHint}>
              <CircleInfoIcon
                width={24}
                height={24}
                className={styles.infoIcon}
              />
              <p className={styles.text}>
                {planType === RENEWABLE
                  ? t('purchase.renewableSubscriptionMessage')
                  : t('purchase.nonRenewableSubscriptionMessage')}
              </p>
            </div>
          )}

          {showErrorModal && (
            <Modal onClose={this.onCloseErrorModal} close={showErrorModal}>
              <div className={styles.dialogContainer}>
                <SimpleDialog
                  title={t('purchase.braintree.errorDialog.title')}
                  description={errorString}
                  theme={whiteDialogTheme}
                >
                  <Button
                    theme={Gray}
                    onClick={toggleReportForm}
                    text={t('purchase.braintree.errorDialog.btnSupport')}
                    ghost
                  />
                  <Button
                    onClick={this.onClickRetryPayment}
                    text={t('purchase.braintree.errorDialog.btnTryAgain')}
                  />
                </SimpleDialog>
              </div>
            </Modal>
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  translate(['purchase', 'navbar']),
  connect(mapStateToProps, {
    fetchBraintreeClientToken,
    fetchPaymentMethodToken,
    toggleReportForm: reportActions.toggleReportForm,
  })
)(BraintreeCheckout);



// WEBPACK FOOTER //
// ./app/components/PaymentHandler/BraintreeCheckout/BraintreeCheckout.jsx